// M141 — /adviezen — 3-sporen adviezen (SEO / CRO / Merkverhaal) per pagina.
// Toont per advies onderbouwing (huidige situatie, verwachte uitkomst,
// impact-schatting) en handelt skip-gevallen af met een groene "scoort al
// goed"-melding. "Ik heb dit gedaan" is nu een toggle.
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import api from "@/lib/api";
import { Loader2, Check, Lightbulb, MapPin, RefreshCw, Info, CheckCircle2, Undo2 } from "lucide-react";
import _brand from "@/utils/normalizeBrand";

const TRACKS = [
  { key: "seo", label: "SEO", color: "text-accent-teal" },
  { key: "cro", label: "CRO", color: "text-pos-green" },
  { key: "brand", label: "Merkverhaal", color: "text-warn-amber" },
];

function Onderbouwing({ ond }) {
  if (!ond || (!ond.huidige_situatie && !ond.verwachte_uitkomst && !ond.impact_schatting)) return null;
  return (
    <div data-testid="adviezen-onderbouwing"
          className="rounded border border-border-hairline bg-bg-panel p-3 space-y-2 text-xs">
      <div className="text-[10px] text-text-muted">Waarom dit werkt</div>
      {ond.huidige_situatie && (
        <div>
          <span className="text-text-muted">Huidige situatie: </span>
          <span className="text-text-primary">{_brand(ond.huidige_situatie)}</span>
        </div>
      )}
      {ond.verwachte_uitkomst && (
        <div>
          <span className="text-text-muted">Verwachte uitkomst: </span>
          <span className="text-text-primary">{_brand(ond.verwachte_uitkomst)}</span>
        </div>
      )}
      {ond.impact_schatting && (
        <div className="flex items-start gap-1">
          <div>
            <span className="text-text-muted">Impact: </span>
            <span className="text-text-primary">{_brand(ond.impact_schatting)}</span>
          </div>
          <span title="Dit is een schatting op basis van jouw eigen data plus gemiddelden."
                 className="text-text-subtle cursor-help">
            <Info className="w-3 h-3 mt-0.5" />
          </span>
        </div>
      )}
      {ond.data_bronnen?.length > 0 && (
        <div className="text-[10px] text-text-subtle italic">
          Bron: {ond.data_bronnen.map(_brand).join(", ")}
        </div>
      )}
    </div>
  );
}

export default function AdviezenPage() {
  const { hideInnerHeader } = useEmbed();
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [activeTrack, setActiveTrack] = useState("seo");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pages/top10");
        setPages(data.items || []);
        const initial = new URLSearchParams(window.location.search).get("page");
        const first = initial || (data.items?.[0]?.page_id);
        if (first) setSelected(first);
      } catch (_e) { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      setBusy(true); setAdvice(null);
      try { const { data } = await api.get(`/pages/${selected}/advice`); setAdvice(data); }
      catch (_e) { setAdvice({ items: [] }); }
      finally { setBusy(false); }
    })();
  }, [selected]);

  const regen = async () => {
    if (!selected) return;
    setBusy(true);
    try { const { data } = await api.post(`/pages/${selected}/advice/regenerate`); setAdvice(data); }
    catch (_e) {}
    finally { setBusy(false); }
  };

  const patchLocal = (adviceId, patch) => {
    setAdvice(a => ({
      ...a,
      items: a.items.map(x => x.advice_id === adviceId ? { ...x, ...patch } : x),
    }));
  };

  const markDone = async (adviceId) => {
    try {
      await api.post(`/pages/advice/${adviceId}/done`);
      patchLocal(adviceId, { status: "measuring", done_at: new Date().toISOString() });
    } catch (_e) {}
  };

  const undoDone = async (adviceId) => {
    try {
      await api.post(`/pages/advice/${adviceId}/undo`);
      patchLocal(adviceId, { status: "open", done_at: null });
    } catch (_e) {}
  };

  const trackItems = (advice?.items || []).filter(x => x.track === activeTrack);
  const skipItem = trackItems.find(x => x.status === "skipped");
  const activeItems = trackItems.filter(x => x.status !== "skipped");

  return (
    <AppLayout>
      {!hideInnerHeader && (
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary tracking-tight flex items-center gap-2"
                 data-testid="adviezen-title">
              <Lightbulb className="w-5 h-5 text-accent-teal" /> Adviezen per pagina
            </h1>
            <p className="text-sm text-text-muted mt-1">
              SEO, CRO en merkverhaal — adviesmodus (alleen-lezen).
            </p>
          </div>
          <button onClick={regen} disabled={!selected || busy}
                   data-testid="adviezen-regenerate"
                   className="text-sm px-3 py-2 rounded-[10px] border border-accent-teal/40 text-accent-teal-dim hover:bg-accent-mint flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Advies opnieuw genereren
          </button>
        </div>
      )}
      {hideInnerHeader && (
        <div className="mb-4 flex justify-end">
          <button onClick={regen} disabled={!selected || busy}
                   data-testid="adviezen-regenerate"
                   className="text-sm px-3 py-2 rounded-[10px] border border-accent-teal/40 text-accent-teal-dim hover:bg-accent-mint flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Advies opnieuw genereren
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4">
        <aside className="rounded-card border border-border-hairline bg-bg-panel p-2 max-h-[600px] overflow-auto"
                data-testid="adviezen-page-list">
          {pages.length === 0 ? (
            <div className="p-3 text-xs text-text-muted italic">Geen pagina's gevonden.</div>
          ) : pages.map((p, i) => (
            <button key={p.page_id} onClick={() => setSelected(p.page_id)}
                     data-testid={`adviezen-page-item-${i}`}
                     className={`w-full text-left px-3 py-2 rounded text-xs mb-1 ${ selected === p.page_id ? "bg-accent-teal/10 border border-accent-teal/30" : "hover:bg-bg-panel-raised"}`}>
              <div className="text-text-primary font-medium truncate">{p.title || p.url}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-text-muted">{p.nexora_score ?? "—"}</span>
                {TRACKS.map(t => (
                  <span key={t.key}
                         className={`w-1.5 h-1.5 rounded-full ${ (p.component_scores?.[t.key] ?? 0) >= 80 ? "bg-pos-green" : "bg-text-subtle"}`}
                         title={`${t.label}: ${p.component_scores?.[t.key] ?? "?"}`} />
                ))}
              </div>
            </button>
          ))}
        </aside>

        <section className="rounded-card border border-border-hairline bg-bg-panel p-4">
          {!selected ? (
            <div className="text-sm text-text-muted italic">Kies een pagina links.</div>
          ) : (
            <>
              <div className="flex gap-1 mb-4 border-b border-border-hairline">
                {TRACKS.map(t => (
                  <button key={t.key} onClick={() => setActiveTrack(t.key)}
                           data-testid={`adviezen-tab-${t.key}`}
                           className={`px-3 py-2 text-xs text-text-secondary ${
                             activeTrack === t.key
                               ? `border-b-2 border-accent-teal ${t.color}`
                               : "text-text-muted hover:text-text-primary"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              {busy ? (
                <div className="text-text-muted text-sm flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Adviezen laden…
                </div>
              ) : activeItems.length === 0 && !skipItem ? (
                <div className="text-text-muted text-sm italic">Geen open adviezen in dit spoor.</div>
              ) : (
                <div className="space-y-3" data-testid="adviezen-list">
                  {activeItems.length > 0 && (
                    <div>
                      <div className="text-[10px] text-text-muted mb-2">
                        Open adviezen
                      </div>
                      <div className="space-y-3">
                        {activeItems.map((it, i) => (
                    <div key={it.advice_id} data-testid={`adviezen-card-${activeTrack}-${i}`}
                          className="rounded-card border border-border-hairline bg-bg-panel-raised p-4 space-y-2 overflow-hidden">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-3.5 h-3.5 text-accent-teal mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-text-primary font-semibold break-words min-w-0">{_brand(it.location_human)}</div>
                      </div>
                      {it.what_now && (
                        <div>
                          <div className="text-[10px] text-text-muted">Wat nu</div>
                          <div className="text-xs text-text-primary italic break-words">{_brand(it.what_now)}</div>
                        </div>
                      )}
                      <div className="rounded border border-pos-green/40 bg-pos-green/5 p-2">
                        <div className="text-[10px] text-pos-green mb-1">Wat veranderen</div>
                        <div className="text-xs text-text-primary break-words">{_brand(it.what_change)}</div>
                      </div>
                      <Onderbouwing ond={it.onderbouwing} />
                      {it.why && !it.onderbouwing?.huidige_situatie && (
                        <div className="text-[10px] text-text-subtle italic break-words">Waarom: {_brand(it.why)}</div>
                      )}
                      <div className="flex justify-end gap-2 flex-wrap">
                        {it.status === "measuring" ? (
                          <>
                            <span className="text-[11px] px-3 py-1 rounded border border-warn-amber/40 text-warn-amber bg-warn-amber/5">
                              Meting start over 4 weken
                            </span>
                            <button onClick={() => undoDone(it.advice_id)}
                                     data-testid={`adviezen-undo-${activeTrack}-${i}`}
                                     className="text-[11px] px-3 py-1 rounded border border-border-hairline text-text-muted hover:bg-bg-panel-raised flex items-center gap-1">
                              <Undo2 className="w-3 h-3" /> Toch niet
                            </button>
                          </>
                        ) : it.status === "measured" ? (
                          <span className={`text-[11px] px-3 py-1 rounded border ${ it.measured_verdict === "werkt" ? "border-pos-green/40 text-pos-green bg-pos-green/10" : "border-warn-amber/40 text-warn-amber bg-warn-amber/5"}`}>
                            {it.measured_verdict === "werkt"
                              ? `Werkt (+${it.measured_score_delta} punten)`
                              : "Weinig effect — probeer een ander advies"}
                          </span>
                        ) : (
                          <button onClick={() => markDone(it.advice_id)}
                                   data-testid={`adviezen-done-${activeTrack}-${i}`}
                                   className="text-[11px] px-3 py-1 rounded border border-pos-green/40 text-pos-green hover:bg-pos-green/10 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Ik heb dit gedaan
                          </button>
                        )}
                      </div>
                    </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {skipItem && (
                    <div data-testid={`adviezen-skip-${activeTrack}`}
                          className="rounded-card border border-pos-green/40 bg-pos-green/5 p-4 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-pos-green mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-semibold text-pos-green">
                          {activeItems.length > 0
                            ? "Scoort al goed op deze onderdelen"
                            : "Scoort al goed"}
                        </div>
                        <div className="text-xs text-text-primary mt-1">{_brand(skipItem.skip_reason_nl)}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
